import { useMutation } from '@tanstack/react-query';
import type { PollData, PostData } from '@dove/shared';
import { votePoll } from '@/lib/api';
import {
  cancelPostQueries,
  updatePostCaches,
} from '@/lib/post-cache';
import { cn } from '@/lib/utils';

function applyVote(poll: PollData, selectedOptionIds: string[]): PollData {
  const before = new Set(poll.selectedOptionIds);
  const after = new Set(selectedOptionIds);
  return {
    ...poll,
    selectedOptionIds,
    options: poll.options.map((option) => ({
      ...option,
      votes: Math.max(
        0,
        option.votes + Number(after.has(option.id)) - Number(before.has(option.id)),
      ),
    })),
  };
}

/** A poll answer is a complete desired selection, so optimistic retries converge. */
export function Poll({ post }: { post: PostData }) {
  const poll = post.poll;
  const vote = useMutation({
    mutationFn: (selected: string[]) => votePoll(post.id, selected),
    onMutate: async (selected) => {
      await cancelPostQueries();
      const previousPoll = post.poll;
      updatePostCaches(post.id, (cached) =>
        cached.poll
          ? { ...cached, poll: applyVote(cached.poll, selected) }
          : cached,
      );
      return { previousPoll };
    },
    onError: (_error, _selected, context) => {
      if (!context?.previousPoll) return;
      updatePostCaches(post.id, (cached) => ({
        ...cached,
        poll: context.previousPoll,
      }));
    },
  });
  if (!poll) return null;

  const selected = poll.selectedOptionIds;
  const total = poll.options.reduce((sum, option) => sum + option.votes, 0);
  const choose = (id: string) => {
    if (vote.isPending) return;
    vote.reset();
    const next = poll.multiple
      ? selected.includes(id)
        ? selected.filter((option) => option !== id)
        : [...selected, id]
      : selected[0] === id
        ? []
        : [id];
    vote.mutate(next);
  };

  return (
    <section
      className="relative z-20 mt-3 rounded-xl border border-border bg-muted/30 p-3"
      aria-label={poll.multiple ? 'Multiple choice poll' : 'Poll'}
    >
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        {poll.multiple ? 'Choose all that apply' : 'Choose one answer'}
      </p>
      <div className="space-y-2">
        {poll.options.map((option) => {
          const checked = selected.includes(option.id);
          const percentage = total ? Math.round((option.votes / total) * 100) : 0;
          return (
            <button
              key={option.id}
              type="button"
              disabled={vote.isPending}
              onClick={() => choose(option.id)}
              className={cn(
                'relative flex w-full items-center gap-2 overflow-hidden rounded-lg border px-3 py-2 text-start text-sm transition-colors',
                checked
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:bg-muted',
              )}
              aria-pressed={checked}
            >
              <span
                className={cn(
                  'relative z-10 flex size-4 shrink-0 items-center justify-center border border-current',
                  poll.multiple ? 'rounded' : 'rounded-full',
                  checked && 'bg-primary text-primary-foreground',
                )}
                aria-hidden
              >
                {checked && '✓'}
              </span>
              <span className="relative z-10 min-w-0 flex-1" dir="auto">
                {option.text}
              </span>
              {total > 0 && (
                <span className="relative z-10 text-xs text-muted-foreground">
                  {percentage}%
                </span>
              )}
              {total > 0 && (
                <span
                  className="absolute inset-y-0 start-0 bg-muted"
                  style={{ width: `${percentage}%` }}
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>
      {total > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {total} {total === 1 ? 'vote' : 'votes'}
        </p>
      )}
      {vote.isError && (
        <p className="mt-2 text-xs text-destructive">Your answer didn’t save.</p>
      )}
    </section>
  );
}
